import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ShoppingBag, Check, ArrowLeft } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { publicAPI } from '../../lib/api';
import { useCart } from '../../contexts/CartContext';
import { formatPrice, calculateSavingsPercent } from '../../lib/utils';
import { trackViewContent } from '../../lib/tracking';
import { toast } from 'sonner';

export default function LookDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addItem, openCart } = useCart();
  
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    loadLook();
  }, [slug]);

  const loadLook = async () => {
    setLoading(true);
    try {
      const response = await publicAPI.getLook(slug);
      setLook(response.data);
      
      // Track view
      trackViewContent({
        id: response.data.id,
        name: response.data.name,
        category: response.data.style,
        type: 'look',
        price: response.data.bundle_price_cents
      });
    } catch (error) {
      console.error('Error loading look:', error);
      navigate('/looks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addItem({
        look_id: look.id,
        quantity: 1,
        name: look.name
      });
      toast.success('Look ajouté au panier !');
      openCart();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout au panier');
    } finally {
      setAdding(false);
    }
  };

  const handleAddProduct = async (product) => {
    const variant = product?.variants?.[0];
    if (!variant) {
      toast.error('Aucune variante disponible pour ce produit');
      return;
    }

    try {
      await addItem({
        product_id: product.id,
        variant_id: variant.id,
        quantity: 1,
      });
      toast.success('Produit ajouté au panier !');
      openCart();
    } catch (error) {
      console.error('Error adding product to cart:', error);
      toast.error('Erreur lors de l\'ajout du produit');
    }
  };

  if (loading) {
    return (
      <StoreLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-[3/4] bg-muted animate-pulse rounded" />
            <div className="space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded w-2/3" />
              <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
              <div className="h-20 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </StoreLayout>
    );
  }

  if (!look) {
    return null;
  }

  const savingsPercent = calculateSavingsPercent(
    look.original_price_cents,
    look.bundle_price_cents
  );

  return (
    <StoreLayout>
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate('/looks')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour aux looks
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              <div className="aspect-[3/4] overflow-hidden bg-muted">
                <img
                  src={look.images?.[selectedImage] || 'https://images.unsplash.com/photo-1652281846260-14c1bdd5e9a0?w=1200'}
                  alt={look.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {look.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {look.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-foreground' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Tags */}
              <div className="flex gap-2">
                {look.style && (
                  <Badge variant="secondary">{look.style}</Badge>
                )}
                {look.occasion && (
                  <Badge variant="outline">{look.occasion}</Badge>
                )}
                {look.gender && (
                  <Badge variant="outline">{look.gender}</Badge>
                )}
              </div>

              {/* Title */}
              <h1 className="font-display text-3xl sm:text-4xl font-bold" data-testid="look-title">
                {look.name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-semibold" data-testid="look-price">
                  {formatPrice(look.bundle_price_cents)}
                </span>
                {look.original_price_cents > look.bundle_price_cents && (
                  <>
                    <span className="text-xl text-muted-foreground line-through">
                      {formatPrice(look.original_price_cents)}
                    </span>
                    <Badge className="bg-green-600">
                      -{savingsPercent}%
                    </Badge>
                  </>
                )}
              </div>

              {/* Savings */}
              {look.savings_cents > 0 && (
                <div className="bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 px-4 py-3 rounded">
                  <span className="font-medium">
                    {t('looks.bundle.savings')} {formatPrice(look.savings_cents)}
                  </span>
                  {' '}en achetant ce look complet
                </div>
              )}

              {/* Description */}
              {look.description && (
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {look.description}
                </p>
              )}

              {/* Add to Cart */}
              <Button
                size="lg"
                className="w-full sm:w-auto group"
                onClick={handleAddToCart}
                disabled={adding}
                data-testid="add-to-cart-btn"
              >
                {adding ? (
                  'Ajout en cours...'
                ) : (
                  <>
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    {t('looks.bundle.add')}
                  </>
                )}
              </Button>

              {/* Included Items */}
              {look.products && look.products.length > 0 && (
                <div className="pt-8 border-t border-border">
                  <h3 className="font-semibold text-lg mb-4">
                    {t('looks.items.included')} ({look.products.length})
                  </h3>
                  <div className="space-y-4">
                    {look.products.map((product) => (
                      <div 
                        key={product.id} 
                        className="flex gap-4 p-3 bg-muted/50 rounded"
                        data-testid={`included-product-${product.id}`}
                      >
                        <Link
                          to={`/products/${product.slug || product.id}`}
                          className="w-16 h-20 bg-muted overflow-hidden flex-shrink-0 block"
                        >
                          <img
                            src={product.images?.[0] || 'https://via.placeholder.com/100'}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/products/${product.slug || product.id}`}
                            className="font-medium truncate hover:underline"
                          >
                            {product.name}
                          </Link>
                          <p className="text-sm text-muted-foreground">{product.category}</p>
                          <p className="text-sm font-medium mt-1">
                            {formatPrice(product.variants?.[0]?.price_cents || 0)}
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => handleAddProduct(product)}
                          >
                            <ShoppingBag className="h-4 w-4 mr-1" />
                            Ajouter ce produit
                          </Button>
                        </div>
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-green-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </StoreLayout>
  );
}
